/**
 * Camada central de notificações (toasts) do EDULumina.
 *
 * Padroniza o uso do `sonner` em todo o sistema garantindo:
 *   - Tipos fixos: success / info / error / warning.
 *   - Assinatura compatível com sonner: (titulo, descricaoOuOpcoes?, opcoes?).
 *   - Tom consistente (título curto + descrição contextual opcional).
 *
 * Diretrizes de uso:
 *   - notifySuccess → ação concluída pelo usuário (salvou, enviou, criou).
 *   - notifyInfo    → mudança de estado/contexto (filtro restaurado, item carregado).
 *   - notifyWarning → atenção / pré-condição não atendida.
 *   - notifyError   → falha real (request rejeitada, validação fatal).
 *
 * Para erros do Supabase, prefira `notifyErroSupabase(titulo, err, contexto?)` ou `notifyError(titulo, traduzirErroSupabase(e))`.
 *
 * Casos avançados do sonner (`toast.promise`, `toast.loading`,
 * `toast.dismiss`, `toast.custom`) seguem importados diretamente de `sonner`
 * — não são padronizados aqui pois fogem do modelo título+descrição+tipo.
 */
import { toast, type ExternalToast } from 'sonner';
import { traduzirErro } from './erroAmigavel';

/** Opções extras (ações, id, duração, etc.). Igual ao tipo do sonner. */
export type NotifyOptions = ExternalToast;

/**
 * Resolve o segundo argumento aceitando string (descrição) ou objeto de opções
 * (compatível com a API original do sonner).
 */
function buildOptions(
  descriptionOrOptions?: string | NotifyOptions,
  options?: NotifyOptions,
): ExternalToast | undefined {
  if (typeof descriptionOrOptions === 'string') {
    return { ...(options ?? {}), description: descriptionOrOptions };
  }
  if (descriptionOrOptions && typeof descriptionOrOptions === 'object') {
    return { ...descriptionOrOptions, ...(options ?? {}) };
  }
  return options;
}

/** Ação concluída com sucesso. */
export function notifySuccess(
  title: string,
  descriptionOrOptions?: string | NotifyOptions,
  options?: NotifyOptions,
) {
  // Se o título não começa com "Pronto!", adicionamos como prefixo padrão e movemos o original para a descrição se não houver uma
  let finalTitle = title;
  let finalOptions = buildOptions(descriptionOrOptions, options);

  if (!title.startsWith('Pronto!')) {
    finalTitle = 'Pronto!';
    if (!finalOptions?.description) {
      finalOptions = { ...(finalOptions ?? {}), description: title };
    }
  }

  return toast.success(finalTitle, finalOptions);
}

/** Mudança de estado/contexto informativa. */
export function notifyInfo(
  title: string,
  descriptionOrOptions?: string | NotifyOptions,
  options?: NotifyOptions,
) {
  let finalTitle = title;
  let finalOptions = buildOptions(descriptionOrOptions, options);

  if (!title.startsWith('Informação!')) {
    finalTitle = 'Informação!';
    if (!finalOptions?.description) {
      finalOptions = { ...(finalOptions ?? {}), description: title };
    }
  }

  return toast.info(finalTitle, finalOptions);
}

/** Atenção: pré-condição não atendida ou ação reversível com risco. */
export function notifyWarning(
  title: string,
  descriptionOrOptions?: string | NotifyOptions,
  options?: NotifyOptions,
) {
  let finalTitle = title;
  let finalOptions = buildOptions(descriptionOrOptions, options);

  if (!title.startsWith('Atenção!')) {
    finalTitle = 'Atenção!';
    if (!finalOptions?.description) {
      finalOptions = { ...(finalOptions ?? {}), description: title };
    }
  }

  return toast.warning(finalTitle, finalOptions);
}

/** Falha em operação. */
export function notifyError(
  title: string,
  descriptionOrOptions?: string | NotifyOptions,
  options?: NotifyOptions,
) {
  let finalTitle = title;
  let finalOptions = buildOptions(descriptionOrOptions, options);

  if (!title.startsWith('Erro!')) {
    finalTitle = 'Erro!';
    if (!finalOptions?.description) {
      finalOptions = { ...(finalOptions ?? {}), description: title };
    }
  }

  return toast.error(finalTitle, finalOptions);
}

/**
 * Alias semântico de `traduzirErro` para deixar explícito que o erro veio
 * do Supabase (PostgREST, Auth, Storage ou Edge Functions). Encapsula a
 * mesma lógica de tradução PT-BR amigável.
 *
 * @param err     - Erro recebido do SDK do Supabase ou de uma RPC.
 * @param contexto - Texto curto descrevendo a operação (ex.: "salvar turma").
 */
export function traduzirErroSupabase(err: unknown, contexto?: string): string {
  return traduzirErro(err, contexto);
}

/**
 * Atalho para reportar falhas oriundas do Supabase com mensagem amigável.
 * Equivale a `notifyError(titulo, traduzirErroSupabase(err, contexto))`.
 *
 * @param title    - Título curto exibido no toast (ex.: "Falha ao salvar turma").
 * @param err      - Erro original (PostgrestError, AuthError, Error, etc.).
 * @param contexto - Texto curto descrevendo a ação (ex.: "salvar turma").
 */
export function notifyErroSupabase(
  title: string,
  err: unknown,
  contexto?: string,
  options?: NotifyOptions,
) {
  return notifyError(title, traduzirErroSupabase(err, contexto), options);
}

/** Re-export do `toast` cru para casos avançados (`promise`, `loading`, `dismiss`, `custom`). */
export { toast };

